import { useEffect, useState } from "react";
import { PawPrintIcon } from "lucide-react";

import { StafferPetProfileSprite } from "../staffer-pets/StafferPetProfileSprite";
import { Column, Row } from "@/components";
import { racetrackLanes } from "@/lib/racetrackLanes";

import type { Pet } from "@/types";

/* Constants */

const DUNE_WORM_HEIGHT_PX = 70;
const NO_PET_ICON_SIZE_PX = 28;

// Soft drop shadow so the sprite reads against the dark road band.
const TRACK_PET_SHADOW_CLASS =
  "overflow-visible [filter:drop-shadow(0_1px_3px_rgba(255,255,255,0.18))_drop-shadow(0_0_5px_rgba(255,255,255,0.06))_drop-shadow(0_1px_2px_rgba(0,0,0,0.06))]";

// Racers start at the start gate on mount, then run forward to their position.
const ENTRY_START_PERCENT = 0;
const ENTRY_DURATION_MS = 2000;

/* Render */

export const RacetrackPetRacer = ({
  progressPercent,
  laneZIndex = 1,
  pet,
}: {
  // 0–100 position along the lane track.
  progressPercent: number;
  // Higher lanes (lower on screen) stack in front of earlier lanes.
  laneZIndex?: number;
  pet?: Pet;
}) => {
  /* React Hooks */
  const [hasEntered, setHasEntered] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(motionQuery.matches);

    if (motionQuery.matches) {
      setHasEntered(true);
      return;
    }

    // Double rAF: lets the browser paint the start-gate frame before we flip to
    // the target, so the run-forward transition actually runs (not a jump).
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        setHasEntered(true);
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  /* Helpers */
  const petHeightPx =
    pet?.equipped.petId === "DUNE_WORM"
      ? DUNE_WORM_HEIGHT_PX
      : racetrackLanes.PET_HEIGHT_PX;
  const displayProgress =
    prefersReducedMotion || hasEntered ? progressPercent : ENTRY_START_PERCENT;

  /* Render */
  const renderPet = () => {
    if (!pet) {
      return (
        <PawPrintIcon
          className="text-white/50"
          style={{ width: NO_PET_ICON_SIZE_PX, height: NO_PET_ICON_SIZE_PX }}
        />
      );
    }

    return (
      <StafferPetProfileSprite pet={pet} sizePx={petHeightPx} isOnLeaderboard />
    );
  };

  return (
    <Column
      className="absolute top-1/2 w-max -translate-x-full -translate-y-1/2"
      style={{
        left: `${displayProgress.toString()}%`,
        zIndex: laneZIndex,
        transition: prefersReducedMotion
          ? undefined
          : `left ${ENTRY_DURATION_MS.toString()}ms linear`,
      }}
    >
      <Row className={TRACK_PET_SHADOW_CLASS}>{renderPet()}</Row>
    </Column>
  );
};
